/**
 * MCOS admin, Legal (Treatment Plan spec v1.4, S-28), and blocked drugs
 * (PR-008). SUPERADMIN only.
 *
 * Texts move from draft to approved with who and when; until counsel approves
 * them no plan can be signed or accepted on the live site. Credentials wait
 * here for MCOS to verify them. A drug's status changes only here, with a note.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, Card, EmptyState, ErrorState, Field, LoadingRows, PageHeader, Pill, inputClass } from '../components/ui';

interface TextRow { kind: string; version: string; status: string; isTest: boolean; approvedBy: string | null; approvedOn: string | null; subKind?: string; clinic?: string | null; text: string; inUse: boolean }
interface LibraryRow { entryId: string; itemName: string; version: number; status: string; reviewer: string | null; nextReviewOn: string; daysToReview: number; reviewOverdue: boolean; blocksNewPlans: boolean }
interface StateRule { state: string; itemClass: string; visitRequirement: string; visitValidDays: number; pdmpRequired: boolean; pdmpValidDays: number; npAgreementRequired: boolean; aiDisclosureRequired: boolean; ageOfMajority: number | null; notes: string | null; reviewedBy: string; reviewedOn: string }
interface QueueRow { kind: 'license' | 'credential' | 'insurance'; id: string; who: string; clinic: string | null; what: string; expires: string | null }
interface FormularyRow { itemId: string; name: string; category: string; mcosStatus: string; compoundingStatus: string; compoundingNote: string | null; blocked: boolean; lastChange: { at: string; by: string; note: string | null } | null }
interface Hold { id: string; scopeKind: string; scopeId: string; reason: string; placedAt: string; releasedAt: string | null }
interface Conflict { id: string; person: string; counterparty: string; relationship: string; disclosedOn: string; notes: string | null }
interface ChainCheck { id: string; ranAt: string; chainsChecked: number; brokenCount: number; anchorMismatches: number; anchorError: string | null }
interface LegalDesk {
  texts: TextRow[];
  library: LibraryRow[];
  stateRules: StateRule[];
  verification: QueueRow[];
  formulary: FormularyRow[];
  holds: Hold[];
  conflicts: Conflict[];
  chainChecks: ChainCheck[];
  choices: { itemClasses: string[]; visitRequirements: string[]; mcosStatuses: string[]; compoundingStatuses: string[] };
}

const KEY = ['admin-legal'];
const TABS = [
  ['texts', 'Texts'],
  ['verification', 'Verification'],
  ['formulary', 'Drug status'],
  ['state-rules', 'State rules'],
  ['library', 'Protocol library'],
  ['checks', 'Chain checks'],
  ['holds', 'Holds and conflicts'],
] as const;
type Tab = (typeof TABS)[number][0];

const today = () => new Date().toISOString().slice(0, 10);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function statusPill(status: string, isTest = false) {
  const tone = status === 'approved' ? 'bg-mint text-forest' : status === 'retired' ? 'bg-[#eceae4] text-[#5b6b7c]' : 'bg-risk-yellow-soft text-risk-yellow';
  return <Pill className={tone}>{isTest ? `${status}, test` : status}</Pill>;
}

export function PlatformLegalPage() {
  const desk = useQuery({ queryKey: KEY, queryFn: () => api.get<LegalDesk>('/admin/legal') });
  const [tab, setTab] = useState<Tab>('texts');
  const counts: Partial<Record<Tab, number>> = desk.data
    ? {
        texts: desk.data.texts.filter((t) => t.status === 'draft').length,
        verification: desk.data.verification.length,
        library: desk.data.library.filter((l) => l.reviewOverdue).length,
        checks: desk.data.chainChecks[0] && (desk.data.chainChecks[0].brokenCount || desk.data.chainChecks[0].anchorMismatches) ? 1 : 0,
      }
    : {};

  return (
    <div className="mx-auto max-w-[1100px]">
      <PageHeader title="Legal" subtitle="Texts, credentials, drug status and state rules for every clinic." />
      <div className="mb-4 flex flex-wrap gap-1 border-b border-hairline" role="tablist" aria-label="Legal sections">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-[14px] ${tab === id ? 'border-forest font-semibold text-forest' : 'border-transparent text-[#4d6072] hover:text-navy'}`}
          >
            {label}
            {counts[id] ? <span className="rounded-full bg-risk-yellow-soft px-1.5 text-[11px] font-semibold text-risk-yellow">{counts[id]}</span> : null}
          </button>
        ))}
      </div>
      {desk.isLoading && <LoadingRows rows={5} />}
      {desk.error && <ErrorState error={desk.error} retry={() => void desk.refetch()} />}
      {desk.data && (
        <>
          {tab === 'texts' && <Texts rows={desk.data.texts} />}
          {tab === 'verification' && <Verification rows={desk.data.verification} />}
          {tab === 'formulary' && <Formulary rows={desk.data.formulary} choices={desk.data.choices} />}
          {tab === 'state-rules' && <StateRules rows={desk.data.stateRules} choices={desk.data.choices} />}
          {tab === 'library' && <Library rows={desk.data.library} />}
          {tab === 'checks' && <Checks rows={desk.data.chainChecks} />}
          {tab === 'holds' && <HoldsAndConflicts holds={desk.data.holds} conflicts={desk.data.conflicts} />}
        </>
      )}
    </div>
  );
}

/** Every change answers with fresh rows; merge them into the cached desk. */
function useDeskMutation<T>(call: (input: T) => Promise<Partial<LegalDesk>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: (fresh) => {
      qc.setQueryData<LegalDesk>(KEY, (was) => (was ? { ...was, ...fresh } : was));
      void qc.invalidateQueries({ queryKey: ['opr-items'] });
    },
  });
}

function Failure({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="text-sm text-red-600" role="alert">{error instanceof Error ? error.message : 'That did not save.'}</p>;
}

function Texts({ rows }: { rows: TextRow[] }) {
  const [open, setOpen] = useState<string | null>(null);
  const approve = useDeskMutation((t: { kind: string; version: string }) => api.post<Partial<LegalDesk>>('/admin/legal/texts/approve', t));
  const retire = useDeskMutation((t: { kind: string; version: string }) => api.post<Partial<LegalDesk>>('/admin/legal/texts/retire', t));
  const label: Record<string, string> = { attestation: 'Prescriber attestation', terms: 'Prescriber Terms', consent: 'Patient consent' };
  return (
    <Card title="Texts" subtitle="Approve only the wording counsel has signed off. Until then no plan can be signed or accepted on the live site.">
      <div className="space-y-2">
        {rows.map((row) => {
          const id = `${row.kind}:${row.version}`;
          return (
            <div key={id} className="rounded-md border border-hairline px-4 py-3" data-testid={`text-${row.version}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-[14px] font-semibold text-navy">
                    {label[row.kind] ?? row.kind}
                    {row.subKind === 'hrt_form' ? ', hormone therapy form' : ''}
                    {row.clinic ? `, ${row.clinic}'s own` : ''} <span className="font-normal text-[#5b6b7c]">{row.version}</span>
                  </p>
                  {row.approvedBy && <p className="text-[12px] text-[#5b6b7c]">Approved by {row.approvedBy} on {row.approvedOn}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {row.inUse && <Pill className="bg-[#e6eef7] text-navy">in use</Pill>}
                  {statusPill(row.status, row.isTest)}
                  <Button size="sm" variant="secondary" onClick={() => setOpen(open === id ? null : id)}>
                    {open === id ? 'Hide' : 'Read'}
                  </Button>
                  {row.status === 'draft' && (
                    <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate({ kind: row.kind, version: row.version })}>
                      Approve
                    </Button>
                  )}
                  {row.status === 'approved' && !row.isTest && (
                    <Button size="sm" variant="secondary" loading={retire.isPending} onClick={() => retire.mutate({ kind: row.kind, version: row.version })}>
                      Retire
                    </Button>
                  )}
                </div>
              </div>
              {open === id && <div className="mt-3 max-h-[260px] overflow-y-auto whitespace-pre-line rounded bg-[#f5f8fb] p-3 text-[12.5px] leading-relaxed">{row.text}</div>}
            </div>
          );
        })}
        <Failure error={approve.error ?? retire.error} />
      </div>
    </Card>
  );
}

function Verification({ rows }: { rows: QueueRow[] }) {
  const [source, setSource] = useState('');
  const verify = useDeskMutation((r: { kind: string; id: string }) => api.post<Partial<LegalDesk>>('/admin/legal/verify', { ...r, source }));
  if (rows.length === 0) return <Card><EmptyState title="Nothing waiting" body="Every licence, credential and certificate on file is verified." /></Card>;
  return (
    <Card title="Waiting for MCOS check" subtitle="Check each against its board or carrier, then mark it verified. Signing stops on unverified licences.">
      <Field label="How it was checked" hint="For example: Texas Medical Board lookup, 6 Oct 2026">
        <input id="verify-source" className={inputClass} value={source} onChange={(e) => setSource(e.target.value)} />
      </Field>
      <ul className="mt-3 divide-y divide-hairline">
        {rows.map((row) => (
          <li key={`${row.kind}-${row.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`verify-${row.id}`}>
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-navy">{row.who}</p>
              <p className="text-[12.5px] text-[#5b6b7c]">
                {row.what}
                {row.expires ? `, expires ${row.expires}` : ''}
              </p>
            </div>
            <Button size="sm" disabled={source.trim().length < 3} loading={verify.isPending} onClick={() => verify.mutate({ kind: row.kind, id: row.id })}>
              Mark verified
            </Button>
          </li>
        ))}
      </ul>
      <Failure error={verify.error} />
    </Card>
  );
}

function Formulary({ rows, choices }: { rows: FormularyRow[]; choices: LegalDesk['choices'] }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ mcosStatus: '', compoundingStatus: '', note: '' });
  const change = useDeskMutation((r: { itemId: string }) =>
    api.put<{ item: FormularyRow }>(`/admin/legal/formulary/${r.itemId}`, draft).then(({ item }) => ({ formulary: rows.map((row) => (row.itemId === item.itemId ? item : row)) })),
  );
  return (
    <Card title="Drug status" subtitle="Retired, Not allowed and Pending FDA action drugs are hidden from every clinic. Every change needs a note.">
      <ul className="divide-y divide-hairline">
        {rows.map((row) => (
          <li key={row.itemId} className="py-2.5" data-testid={`drug-${row.itemId}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[14px] font-medium text-navy">
                  {row.name} <span className="font-normal text-[#5b6b7c]">{row.category}</span>
                </p>
                <p className="text-[12.5px] text-[#5b6b7c]">
                  {row.mcosStatus}, {row.compoundingStatus}
                  {row.lastChange ? `. Changed ${when(row.lastChange.at)} by ${row.lastChange.by}: ${row.lastChange.note ?? ''}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {row.blocked ? <Pill className="bg-alert-soft text-alert">blocked</Pill> : <Pill className="bg-mint text-forest">prescribable</Pill>}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditing(editing === row.itemId ? null : row.itemId);
                    setDraft({ mcosStatus: row.mcosStatus, compoundingStatus: row.compoundingStatus, note: '' });
                  }}
                >
                  {editing === row.itemId ? 'Cancel' : 'Change'}
                </Button>
              </div>
            </div>
            {editing === row.itemId && (
              <div className="mt-2 grid gap-2 sm:grid-cols-[160px_220px_1fr_auto] sm:items-end">
                <Field label="MCOS status">
                  <select className={inputClass} value={draft.mcosStatus} onChange={(e) => setDraft({ ...draft, mcosStatus: e.target.value })}>
                    {choices.mcosStatuses.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Compounding status">
                  <select className={inputClass} value={draft.compoundingStatus} onChange={(e) => setDraft({ ...draft, compoundingStatus: e.target.value })}>
                    {choices.compoundingStatuses.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Why">
                  <input className={inputClass} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="The source and the reason" />
                </Field>
                <Button disabled={draft.note.trim().length < 10} loading={change.isPending} onClick={() => change.mutate({ itemId: row.itemId }, { onSuccess: () => setEditing(null) })}>
                  Save
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <Failure error={change.error} />
    </Card>
  );
}

const blankRule = (): StateRule => ({
  state: '', itemClass: 'standard', visitRequirement: 'async_ok', visitValidDays: 365, pdmpRequired: false, pdmpValidDays: 30,
  npAgreementRequired: true, aiDisclosureRequired: false, ageOfMajority: null, notes: null, reviewedBy: '', reviewedOn: today(),
});

function StateRules({ rows, choices }: { rows: StateRule[]; choices: LegalDesk['choices'] }) {
  const [rule, setRule] = useState<StateRule | null>(null);
  const save = useDeskMutation((r: StateRule) => api.put<Partial<LegalDesk>>('/admin/legal/state-rules', r));
  const remove = useDeskMutation((r: { state: string; itemClass: string }) => api.delete<Partial<LegalDesk>>(`/admin/legal/state-rules/${r.state}/${r.itemClass}`));
  const set = <K extends keyof StateRule>(k: K, v: StateRule[K]) => rule && setRule({ ...rule, [k]: v });
  return (
    <Card title="State rules" subtitle="Counsel fills these. A state and item class with no row uses the safe default: in person, until a rule says otherwise." action={<Button size="sm" onClick={() => setRule(blankRule())}>Add a rule</Button>}>
      {rule && (
        <div className="mb-4 rounded-md border border-hairline p-3" data-testid="rule-form">
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="State"><input className={inputClass} maxLength={2} value={rule.state} onChange={(e) => set('state', e.target.value.toUpperCase())} /></Field>
            <Field label="Item class">
              <select className={inputClass} value={rule.itemClass} onChange={(e) => set('itemClass', e.target.value)}>{choices.itemClasses.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select>
            </Field>
            <Field label="Visit">
              <select className={inputClass} value={rule.visitRequirement} onChange={(e) => set('visitRequirement', e.target.value)}>{choices.visitRequirements.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}</select>
            </Field>
            <Field label="Visit valid, days"><input className={inputClass} inputMode="numeric" value={rule.visitValidDays} onChange={(e) => set('visitValidDays', Number(e.target.value) || 0)} /></Field>
            <Field label="PDMP check valid, days"><input className={inputClass} inputMode="numeric" value={rule.pdmpValidDays} onChange={(e) => set('pdmpValidDays', Number(e.target.value) || 0)} /></Field>
            <Field label="Age of majority" hint="Standard class only. Empty for 18."><input className={inputClass} inputMode="numeric" value={rule.ageOfMajority ?? ''} onChange={(e) => set('ageOfMajority', e.target.value ? Number(e.target.value) : null)} /></Field>
            <Field label="Reviewed by"><input className={inputClass} value={rule.reviewedBy} onChange={(e) => set('reviewedBy', e.target.value)} /></Field>
            <Field label="Reviewed on"><input type="date" className={inputClass} value={rule.reviewedOn} onChange={(e) => set('reviewedOn', e.target.value)} /></Field>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-[13.5px]">
            {([['pdmpRequired', 'PDMP check required'], ['npAgreementRequired', 'NP or PA agreement required'], ['aiDisclosureRequired', 'AI disclosure required']] as const).map(([k, text]) => (
              <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={rule[k]} onChange={(e) => set(k, e.target.checked)} />{text}</label>
            ))}
          </div>
          <Field label="Notes"><input className={inputClass} value={rule.notes ?? ''} onChange={(e) => set('notes', e.target.value || null)} /></Field>
          <div className="mt-3 flex gap-2">
            <Button disabled={rule.state.length !== 2 || rule.reviewedBy.trim().length < 2} loading={save.isPending} onClick={() => save.mutate(rule, { onSuccess: () => setRule(null) })}>Save rule</Button>
            <Button variant="secondary" onClick={() => setRule(null)}>Cancel</Button>
          </div>
          <Failure error={save.error} />
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState title="No state rules yet" body="Every state uses the safe defaults until counsel adds its rules." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.08em] text-[#5b6b7c]"><th className="py-2">State</th><th>Class</th><th>Visit</th><th>PDMP</th><th>NP or PA</th><th>Reviewed</th><th /></tr></thead>
            <tbody className="divide-y divide-hairline">
              {rows.map((r) => (
                <tr key={`${r.state}-${r.itemClass}`}>
                  <td className="py-2 font-semibold">{r.state}</td>
                  <td>{r.itemClass.replace(/_/g, ' ')}</td>
                  <td>{r.visitRequirement.replace(/_/g, ' ')}, {r.visitValidDays} days</td>
                  <td>{r.pdmpRequired ? `yes, ${r.pdmpValidDays} days` : 'no'}</td>
                  <td>{r.npAgreementRequired ? 'yes' : 'no'}</td>
                  <td>{r.reviewedBy}, {r.reviewedOn}</td>
                  <td className="whitespace-nowrap text-right">
                    <Button size="sm" variant="secondary" onClick={() => setRule(r)}>Edit</Button>{' '}
                    <Button size="sm" variant="secondary" loading={remove.isPending} onClick={() => remove.mutate({ state: r.state, itemClass: r.itemClass })}>Remove</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Failure error={remove.error} />
    </Card>
  );
}

function Library({ rows }: { rows: LibraryRow[] }) {
  const [reviewer, setReviewer] = useState('');
  const [next, setNext] = useState(() => new Date(Date.now() + 180 * 864e5).toISOString().slice(0, 10));
  const mark = useDeskMutation((r: { entryId: string }) => api.post<Partial<LegalDesk>>(`/admin/legal/library/${r.entryId}/reviewed`, { reviewer, nextReviewOn: next }));
  const sorted = [...rows].sort((a, b) => a.daysToReview - b.daysToReview);
  return (
    <Card title="Protocol library" subtitle="MCOS Protocol Library · clinical reference for licensed clinicians · not a recommendation. An entry more than 30 days past its review date cannot be added to a new plan.">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Reviewer"><input className={inputClass} value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder="Dr. Joe" /></Field>
        <Field label="Next review on"><input type="date" className={inputClass} value={next} onChange={(e) => setNext(e.target.value)} /></Field>
      </div>
      <ul className="mt-3 divide-y divide-hairline">
        {sorted.map((row) => (
          <li key={row.entryId} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
            <div>
              <p className="text-[14px] font-medium text-navy">{row.itemName} <span className="font-normal text-[#5b6b7c]">v{row.version}</span></p>
              <p className="text-[12.5px] text-[#5b6b7c]">Review {row.reviewOverdue ? 'overdue since' : 'due'} {row.nextReviewOn}{row.reviewer ? `, last by ${row.reviewer}` : ''}</p>
            </div>
            <div className="flex items-center gap-2">
              {row.blocksNewPlans ? <Pill className="bg-alert-soft text-alert">blocks new plans</Pill> : row.reviewOverdue ? <Pill className="bg-risk-yellow-soft text-risk-yellow">overdue</Pill> : null}
              <Button size="sm" variant="secondary" disabled={reviewer.trim().length < 2} loading={mark.isPending} onClick={() => mark.mutate({ entryId: row.entryId })}>Mark reviewed</Button>
            </div>
          </li>
        ))}
      </ul>
      <Failure error={mark.error} />
    </Card>
  );
}

function Checks({ rows }: { rows: ChainCheck[] }) {
  const run = useDeskMutation(() => api.post<Partial<LegalDesk>>('/admin/legal/chain-check'));
  return (
    <Card title="Chain checks" subtitle="Every night the legal audit trail is verified end to end and its newest hashes are copied outside the database." action={<Button size="sm" loading={run.isPending} onClick={() => run.mutate(undefined)}>Run a check now</Button>}>
      {rows.length === 0 ? (
        <EmptyState title="No checks yet" body="The first one runs tonight, or run one now." />
      ) : (
        <ul className="divide-y divide-hairline">
          {rows.map((r) => {
            const ok = r.brokenCount === 0 && r.anchorMismatches === 0;
            return (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid="chain-check">
                <span className="text-[13.5px] text-navy">{new Date(r.ranAt).toLocaleString('en-US')}, {r.chainsChecked} chains</span>
                <span className="flex items-center gap-2">
                  {r.anchorError && <span className="text-[12px] text-[#5b6b7c]">{r.anchorError}</span>}
                  {ok ? <Pill className="bg-mint text-forest">intact</Pill> : <Pill className="bg-alert-soft text-alert">{r.brokenCount + r.anchorMismatches} problems</Pill>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <Failure error={run.error} />
    </Card>
  );
}

function HoldsAndConflicts({ holds, conflicts }: { holds: Hold[]; conflicts: Conflict[] }) {
  const [hold, setHold] = useState({ scopeKind: 'patient', scopeId: '', reason: '' });
  const [conflict, setConflict] = useState({ person: '', counterparty: '', relationship: '', disclosedOn: today(), notes: '' });
  const place = useDeskMutation(() => api.post<Partial<LegalDesk>>('/admin/legal/holds', hold));
  const release = useDeskMutation((id: string) => api.post<Partial<LegalDesk>>(`/admin/legal/holds/${id}/release`));
  const add = useDeskMutation(() => api.post<Partial<LegalDesk>>('/admin/legal/conflicts', { ...conflict, notes: conflict.notes || null }));
  return (
    <div className="space-y-5">
      <Card title="Litigation holds" subtitle="Nothing under a hold is archived or purged until it is released.">
        <div className="grid gap-3 sm:grid-cols-[140px_1fr_2fr_auto] sm:items-end">
          <Field label="Scope">
            <select className={inputClass} value={hold.scopeKind} onChange={(e) => setHold({ ...hold, scopeKind: e.target.value })}>
              <option value="patient">Patient</option><option value="clinic">Clinic</option><option value="prescriber">Prescriber</option>
            </select>
          </Field>
          <Field label="Record id"><input className={inputClass} value={hold.scopeId} onChange={(e) => setHold({ ...hold, scopeId: e.target.value })} /></Field>
          <Field label="Reason"><input className={inputClass} value={hold.reason} onChange={(e) => setHold({ ...hold, reason: e.target.value })} /></Field>
          <Button disabled={!hold.scopeId.trim() || hold.reason.trim().length < 10} loading={place.isPending} onClick={() => place.mutate(undefined, { onSuccess: () => setHold({ scopeKind: 'patient', scopeId: '', reason: '' }) })}>Place hold</Button>
        </div>
        <List empty="No holds.">
          {holds.map((h) => (
            <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
              <span className="text-[13.5px] text-navy">{h.scopeKind} {h.scopeId}: {h.reason}</span>
              {h.releasedAt ? <Pill className="bg-[#eceae4] text-[#5b6b7c]">released {when(h.releasedAt)}</Pill> : <Button size="sm" variant="secondary" loading={release.isPending} onClick={() => release.mutate(h.id)}>Release</Button>}
            </li>
          ))}
        </List>
        <Failure error={place.error ?? release.error} />
      </Card>
      <Card title="Conflicts register" subtitle="A prescriber's financial interest in a counterparty is shown to the patient in the consent.">
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Person"><input className={inputClass} value={conflict.person} onChange={(e) => setConflict({ ...conflict, person: e.target.value })} /></Field>
          <Field label="Counterparty"><input className={inputClass} value={conflict.counterparty} onChange={(e) => setConflict({ ...conflict, counterparty: e.target.value })} /></Field>
          <Field label="Relationship"><input className={inputClass} value={conflict.relationship} onChange={(e) => setConflict({ ...conflict, relationship: e.target.value })} /></Field>
          <Field label="Disclosed on"><input type="date" className={inputClass} value={conflict.disclosedOn} onChange={(e) => setConflict({ ...conflict, disclosedOn: e.target.value })} /></Field>
        </div>
        <div className="mt-3">
          <Button disabled={conflict.person.trim().length < 2 || conflict.counterparty.trim().length < 2 || conflict.relationship.trim().length < 2} loading={add.isPending} onClick={() => add.mutate(undefined)}>Add to the register</Button>
        </div>
        <List empty="Nothing disclosed.">
          {conflicts.map((c) => (
            <li key={c.id} className="py-2.5 text-[13.5px] text-navy">{c.person} and {c.counterparty}: {c.relationship}, disclosed {c.disclosedOn}</li>
          ))}
        </List>
        <Failure error={add.error} />
      </Card>
    </div>
  );
}

function List({ children, empty }: { children: ReactNode[]; empty: string }) {
  if (children.length === 0) return <p className="mt-3 text-[13px] text-[#5b6b7c]">{empty}</p>;
  return <ul className="mt-3 divide-y divide-hairline">{children}</ul>;
}
